/* eslint-disable @typescript-eslint/no-floating-promises */
import { Box, Card, CardContent, Typography } from '@mui/material'
import React from 'react'
import MultiSelectWithCheckBox from '../../../../components/FormElements/MultiSelectWithCheckBox'
import { type ISurveyTemplate, type SurveyInstructorDetails } from '../../../../models/SurveyTemplate'
import { blue, grey } from '../../../../colors'
import { useForm, FormProvider, Controller, useFormContext } from 'react-hook-form'
import GenericModal from '../../../../components/GenericModal'
import GenericButton from '../../../../components/GenericButton'
import CustomTooltip from '../../../../components/CustomTooltip'
import { ButtonSize, ButtonType } from '../../../../components/GenericButton/styles'

// Only top-level repeated sections should ask the student which instructors to review.
// A copy without an instructorId (TA rows in the admin preview, CE-158) cannot be picked or
// filtered, so it must not demand a selection card it can never satisfy.
export const isSelectableParentSection = (section: any): boolean =>
  section?.parentSurveySectionId == null &&
  section?.isSelectInstructorEnabled === true &&
  section?.isRepeatPerInstructor === true &&
  Boolean(section?.instructorId)

// Offer every participant with a top-level repeated copy of the section (instructors, TAs, support staff).
export const getSectionParticipantOptions = (survey: ISurveyTemplate | null, sectionId: string): SurveyInstructorDetails[] => Array.from(
  new Map(
    (survey?.surveySections ?? [])
      .filter((section: any) =>
        String(section?.surveySectionId) === sectionId &&
        section?.parentSurveySectionId == null &&
        section?.instructorId
      )
      .map((section: any) => [
        String(section.instructorId),
        { id: section.instructorId, title: section.instructorName }
      ])
  ).values()
)

// Build one instructor-selection card per selectable parent section.
const getSelectableParentSections = (survey: ISurveyTemplate | null): Array<{ sectionId: string, name: string, descr: string }> => Array.from(
  new Map(
    (survey?.surveySections ?? [])
      .filter(isSelectableParentSection)
      .map((section: any) => [
        String(section.surveySectionId),
        {
          sectionId: String(section.surveySectionId),
          name: section.title ?? '',
          descr: section.descr ?? ''
        }
      ])
  ).values()
)

// Branched descendants inherit instructor selection from the top-level section subtree they belong to.
const getRootAncestorSectionId = (section: any, sectionById: Map<string, any>): string | undefined => {
  let currentParentId = section?.parentSurveySectionId ? String(section.parentSurveySectionId) : undefined
  let rootAncestorSectionId = section?.surveySectionId ? String(section.surveySectionId) : undefined

  while (currentParentId) {
    rootAncestorSectionId = currentParentId
    const parentSection = sectionById.get(currentParentId)
    currentParentId = parentSection?.parentSurveySectionId
      ? String(parentSection.parentSurveySectionId)
      : undefined
  }

  return rootAncestorSectionId
}

interface UserSelectInstructorsCardProps {
  survey: ISurveyTemplate | null
  setSurvey: (survey: any) => void
  setIsUserSelectEnabled: (isEnabled: boolean) => void
  isStudentExperience?: boolean
}

export const UserSelectInstructorsCard: React.FC<UserSelectInstructorsCardProps> = ({ survey, setSurvey, setIsUserSelectEnabled, isStudentExperience = false }) => {
  const isUserSelectEnabled: Array<{ sectionId: string, name: string, descr: string }> = getSelectableParentSections(survey)
  const methods = useForm()
  const watch = methods.watch
  const [modalOpen, setModalOpen] = React.useState(false)
  const [noSectionAvaliableModalOpen, setNoSectionAvaliableModalOpen] = React.useState(false)
  const [pendingSubmit, setPendingSubmit] = React.useState<(() => void) | null>(null)
  let filteredSections: any[] | undefined
  // Enable button if at least one instructor is selected in any section
  const watchedValues = watch()
  const ifAnySectionAvaliable = isUserSelectEnabled.some(section => {
    const selected = watchedValues?.[section.sectionId]
    return Array.isArray(selected) && selected.length > 0
  })
  const onSubmit = (data: Record<string, any>): void => {
    const result = isUserSelectEnabled?.map(section => ({
      sectionId: section.sectionId,
      instructors: data[section?.sectionId] || []
    }))
    const selectedInstructorIdsByParentSection = new Map(
      result.map(({ sectionId, instructors }) => [
        sectionId,
        new Set((instructors ?? []).map((instructor: any) => String(instructor?.id)))
      ])
    )
    const sectionById = new Map(
      (survey?.surveySections ?? [])
        .filter((section: any) => section?.surveySectionId)
        .map((section: any) => [String(section.surveySectionId), section])
    )

    // Child branch sections inherit the selected instructors from their top-level parent section.
    filteredSections = (survey?.surveySections ?? []).filter((section: any) => {
      const rootAncestorSectionId = getRootAncestorSectionId(section, sectionById)

      if (!rootAncestorSectionId || !selectedInstructorIdsByParentSection.has(rootAncestorSectionId)) {
        return true
      }

      if (!section?.instructorId) {
        return true
      }

      return selectedInstructorIdsByParentSection.get(rootAncestorSectionId)?.has(String(section.instructorId)) ?? false
    })

    setIsUserSelectEnabled(false)
    setSurvey({ ...survey, surveySections: filteredSections })
    setModalOpen(false)
    setPendingSubmit(null)
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    setPendingSubmit(() => async () => { await methods.handleSubmit(onSubmit)() })
    setModalOpen(true)
  }

  const handleModalConfirm = (): void => {
    if (pendingSubmit) {
      pendingSubmit()
    }
  }

  return (
    <>
      <Box sx={{
        mb: 2,
        margin: 'auto',
        width: '60%',
        position: 'sticky',
        top: '50%',
        left: '20%'
      }}>
        <FormProvider {...methods}>
          <form onSubmit={handleFormSubmit}>
            <Card>
              <CardContent>
                <Box sx={{ mb: 5 }}>
                  <Typography fontSize={'20px'} fontWeight={600} color={blue[1100]} >Before you begin</Typography>
                  <Typography color={grey[850]}>Please select the instructors you would like to review for the following sections.</Typography>
                </Box>
                {isUserSelectEnabled.map((section: { sectionId: string, name: string, descr: string }) => (
                  <InstructorCard key={section?.sectionId} sectionId={section?.sectionId} survey={survey} heading={section?.name} description={section?.descr} />
                ))}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 2
                }}>
                  {ifAnySectionAvaliable
                    ? (
                    <GenericButton
                      label="Select"
                      {...(!isStudentExperience
                        ? { type: 'submit' }
                        : {
                            onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                              handleFormSubmit(e as unknown as React.FormEvent<HTMLFormElement>)
                            }
                          })}
                      buttonSize={ButtonSize.Small}
                      buttonType={ButtonType.Primary}
                      disabled={false}
                    />
                      )
                    : (
                    <CustomTooltip title="please select at least one instructor to begin">
                      <span>
                        <GenericButton
                          label="Select"
                          {...(!isStudentExperience
                            ? { type: 'submit' }
                            : {
                                onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                                  handleFormSubmit(e as unknown as React.FormEvent<HTMLFormElement>)
                                }
                              })}
                          buttonSize={ButtonSize.Small}
                          buttonType={ButtonType.Primary}
                          disabled={true}
                        />
                      </span>
                    </CustomTooltip>
                      )}
                </Box>
              </CardContent>
            </Card>
          </form>
        </FormProvider>
        <GenericModal showCloseIcon={false}
          modalWidth='sm'
          isOpen={modalOpen}
          maxWidth={'sm'}
          onClose={() => { setModalOpen(false) }}
          onConfirm={async () => { handleModalConfirm() }}>
          <Typography>
            Please confirm your instructor selections.
          </Typography>
        </GenericModal>
      </Box >
    </>
  )
}

const InstructorCard: React.FC<{ sectionId: string, survey: ISurveyTemplate | null, heading: string, description: string }> = ({ sectionId, survey, heading, description }) => {
  const { control } = useFormContext()
  return (
    <Card sx={{ mb: 4 }}>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.6, textAlign: 'left' }}
          >
            {heading}
          </Typography>
          <Typography
            sx={{ fontWeight: 500, fontSize: 16, lineHeight: 1.6, textAlign: 'left' }}
          >
            {description}
          </Typography>
          <Typography>These are the questions that about your {heading} performance</Typography>
        </Box>
        <Controller
          control={control}
          name={sectionId}
          defaultValue={[]}
          render={({ field }) => (
            <MultiSelectWithCheckBox
              label="Instructor(s)"
              placeholder="None Selected"
              sx={{ fontWeight: 500, color: blue[1100] }}
              options={getSectionParticipantOptions(survey, sectionId)}
              getOptionLabel={(option: any) => option?.title}
              getOptionValue={(option: any) => option?.id}
              showSelectAll
              {...field}
            />
          )}
        />
      </CardContent>
    </Card>
  )
}
